import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export type LatLng = { lat: number; lng: number };

type Props = {
  apiKey: string;
  center?: LatLng;
  zoom?: number;
  onChange: (polygon: LatLng[]) => void;
};

const CONTAINER_STYLE: React.CSSProperties = {
  width: "100%",
  height: "420px",
  borderRadius: "8px",
};

const POLY_STYLE: L.PolylineOptions = {
  color: "#0a66ff",
  weight: 2,
  fillColor: "#0a66ff",
  fillOpacity: 0.2,
};

export default function MapPolygonDraw({
  apiKey,
  center = { lat: 50.0755, lng: 14.4378 },
  zoom = 12,
  onChange,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const polygonLayerRef = useRef<L.Polygon | null>(null);
  const markersRef = useRef<L.CircleMarker[]>([]);
  const [points, setPoints] = useState<LatLng[]>([]);
  const [finished, setFinished] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Init map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current).setView([center.lat, center.lng], zoom);

    const tileUrl = apiKey
      ? `https://api.mapy.cz/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${apiKey}`
      : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

    L.tileLayer(tileUrl, {
      attribution: apiKey
        ? '&copy; <a href="https://www.seznam.cz">Seznam.cz</a>, &copy; <a href="https://www.openstreetmap.org">OSM</a>'
        : '&copy; <a href="https://www.openstreetmap.org">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  // Handle map clicks — debounce single click so dblclick can cancel it
  useEffect(() => {
    const map = mapRef.current;
    if (!map || finished) return;

    let clickTimer: ReturnType<typeof setTimeout> | null = null;

    const onClick = (e: L.LeafletMouseEvent) => {
      if (clickTimer) clearTimeout(clickTimer);
      clickTimer = setTimeout(() => {
        setPoints((prev) => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }]);
        clickTimer = null;
      }, 250);
    };

    const onDblClick = (e: L.LeafletMouseEvent) => {
      if (clickTimer) {
        clearTimeout(clickTimer);
        clickTimer = null;
      }
      L.DomEvent.stopPropagation(e.originalEvent);
      L.DomEvent.preventDefault(e.originalEvent);
      setPoints((prev) => {
        if (prev.length >= 3) {
          setFinished(true);
        }
        return prev;
      });
    };

    map.on("click", onClick);
    map.on("dblclick", onDblClick);
    map.doubleClickZoom.disable();

    return () => {
      if (clickTimer) clearTimeout(clickTimer);
      map.off("click", onClick);
      map.off("dblclick", onDblClick);
      map.doubleClickZoom.enable();
    };
  }, [finished]);

  // Draw polygon / markers on map
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clean previous
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    polygonLayerRef.current?.remove();
    polygonLayerRef.current = null;

    if (points.length === 0) return;

    const latLngs = points.map((p) => L.latLng(p.lat, p.lng));

    if (finished && points.length >= 3) {
      const poly = L.polygon(latLngs, POLY_STYLE).addTo(map);
      polygonLayerRef.current = poly;
      onChangeRef.current(points);
    } else {
      // Draw points + connecting line
      if (latLngs.length >= 2) {
        L.polyline(latLngs, { color: "#0a66ff", weight: 2, dashArray: "6 4" }).addTo(map);
        // Store polyline in markers array for cleanup (it's an overlay)
        // Actually let's track separately — simpler to just use polygon layer ref
      }
      latLngs.forEach((ll) => {
        const m = L.circleMarker(ll, {
          radius: 5,
          color: "#0a66ff",
          fillColor: "#fff",
          fillOpacity: 1,
          weight: 2,
        }).addTo(map);
        markersRef.current.push(m);
      });
    }
  }, [points, finished]);

  const handleClear = useCallback(() => {
    setPoints([]);
    setFinished(false);
    // Clean layers
    const map = mapRef.current;
    if (map) {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      polygonLayerRef.current?.remove();
      polygonLayerRef.current = null;
      // Remove any temp polylines
      map.eachLayer((layer) => {
        if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
          layer.remove();
        }
      });
    }
    onChangeRef.current([]);
  }, []);

  return (
    <div>
      <div ref={containerRef} style={CONTAINER_STYLE} />
      {!finished && points.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <span className="muted">
            {points.length < 3
              ? `${points.length} bod${points.length === 1 ? "" : "y"} — klikněte pro další, min. 3`
              : `${points.length} bodů — dvojklik pro dokončení`}
          </span>
        </div>
      )}
      {finished && (
        <div style={{ marginTop: 12 }}>
          <span className="muted">Polygon: {points.length} bodů</span>
          <button type="button" className="secondary" style={{ marginLeft: 12 }} onClick={handleClear}>
            Smazat polygon
          </button>
        </div>
      )}
    </div>
  );
}
